import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsService, GetUpcomingEventsParams, UpdateEventInput, CreateEventInput } from '../services/eventsService';
import { EVENT_KEYS } from './queryKeys';

export const useUpcomingEvents = (params?: GetUpcomingEventsParams) => {
  return useQuery({
    queryKey: EVENT_KEYS.upcoming(params as Record<string, unknown>),
    queryFn: () => eventsService.getUpcomingEvents(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useEvent = (id: string) => {
  return useQuery({
    queryKey: EVENT_KEYS.detail(id),
    queryFn: () => eventsService.getEventById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useAllEvents = () => {
  return useQuery({
    queryKey: EVENT_KEYS.list,
    queryFn: () => eventsService.listEvents(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventInput }) =>
      eventsService.updateEvent(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.all });
    },
  });
};

export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventInput) => eventsService.createEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.list });
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.all });
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eventsService.deleteEvent(id),
    onSuccess: () => {
      // EVENT_KEYS.all (['events']) is the shared prefix, so this invalidates
      // the list, detail, and upcoming queries in one call.
      queryClient.invalidateQueries({ queryKey: EVENT_KEYS.all });
    },
  });
};
